import React, { useEffect, useState, useMemo, useRef } from "react";
import axiosInstanceExternal from "../../utils/axiosInstanceExternal";
import ReactPaginate from "react-paginate";
import ReactModal from "react-modal";
import Select from "react-select";
import { toast } from "react-toastify";
import Fuse from "fuse.js";
import * as XLSX from "xlsx";

const DomainManagement = () => {
    const leadCreationEnabled = process.env.REACT_APP_ENABLE_LEAD_CREATION === "true";
    // --- State ---
    const [records, setRecords] = useState([]);
    const [filteredRecords, setFilteredRecords] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);

    const [sortField, setSortField] = useState("relevance");
    const [sortOrder, setSortOrder] = useState("desc");

    // --- Filters & search ---
    // --- Advanced filter helpers ---
    const OP_OPTIONS = [
        { value: "eq", label: "Equals" },
        { value: "ne", label: "Does not equal" },
        { value: "has", label: "Contains data" },
        { value: "not_has", label: "Does not contain data" },
        { value: "contains", label: "Contains" },
        { value: "not_contains", label: "Does not contain" },
        { value: "begins", label: "Begins with" },
        { value: "not_begins", label: "Does not begin with" },
        { value: "ends", label: "Ends with" },
        { value: "not_ends", label: "Does not end with" },
    ];

    const isNoop = (f) => !f || (f.op === "eq" && (!f.values || f.values.length === 0));

    const matchOne = (raw, f) => {
        const s = (raw ?? "").toString().trim().toLowerCase();
        const vals = (f.values || []).map((v) => v.value.toLowerCase());
        switch (f.op) {
            case "has":
                return s.length > 0;
            case "not_has":
                return s.length === 0;
            case "eq":
                return vals.length === 0 ? true : vals.includes(s);
            case "ne":
                return vals.length === 0 ? true : !vals.includes(s);
            case "contains":
                return vals.length === 0 ? true : vals.some((v) => s.includes(v));
            case "not_contains":
                return vals.length === 0 ? true : vals.every((v) => !s.includes(v));
            case "begins":
                return vals.length === 0 ? true : vals.some((v) => s.startsWith(v));
            case "not_begins":
                return vals.length === 0 ? true : vals.every((v) => !s.startsWith(v));
            case "ends":
                return vals.length === 0 ? true : vals.some((v) => s.endsWith(v));
            case "not_ends":
                return vals.length === 0 ? true : vals.every((v) => !s.endsWith(v));
            default:
                return true;
        }
    };

    const applyFilter = (rows, field, f) => (isNoop(f) ? rows : rows.filter((r) => matchOne(r[field], f)));
    const [searchTerm, setSearchTerm] = useState("");
    const [countryFilter, setCountryFilter] = useState({ op: "eq", values: [] });
    const [categoryFilter, setCategoryFilter] = useState({ op: "eq", values: [] });
    const [selectedCRM, setSelectedCRM] = useState({ value: "*", label: "All CRM Statuses" });

    const crmOptions = useMemo(
        () => [
            { value: "*", label: "All CRM Statuses" },
            { value: "hasOwner", label: "Recorded in CRM" },
            { value: "noOwner", label: "Not Recorded in CRM" },
        ],
        []
    );

    // --- Modals & form data ---
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

    const [recordToDelete, setRecordToDelete] = useState(null);
    const [recordToUpdate, setRecordToUpdate] = useState({});
    const [recordDetails, setRecordDetails] = useState({});
    const [newRecord, setNewRecord] = useState({});

    // button disabled
    const [emailButtonDisabled, setEmailButtonDisabled] = useState(false);
    const [expandButtonDisabled, setExpandButtonDisabled] = useState(false);
    const [leadButtonDisabled, setLeadButtonDisabled] = useState(false);

    // refresh button
    const [isRefreshing, setIsRefreshing] = useState(false);

    // date filter
    const [startDate, setStartDate] = useState(""); // "YYYY-MM-DD"
    const [endDate, setEndDate] = useState("");
    const [dateField, setDateField] = useState("created_at"); // or "expanded_at"
    const DATE_FIELD_OPTIONS = useMemo(
        () => [
            { value: "created_at", label: "Created" },
            { value: "expanded_at", label: "Expanded" },
        ],
        []
    );
    const [dateFieldChosen, setDateFieldChosen] = useState(false);

    const hasFilters =
        searchTerm.trim() !== "" ||
        !isNoop(countryFilter) ||
        !isNoop(categoryFilter) ||
        selectedCRM.value !== "*" ||
        !!startDate ||
        !!endDate;

    const recordsPerPage = 30;

    const email = localStorage.getItem("email");

    // --- Fetch all records ---
    const fetchRecords = async () => {
        setIsRefreshing(true);
        try {
            const resp = await axiosInstanceExternal.get("/record/records");
            setRecords(resp.data);
        } catch (err) {
            console.error(err);
            toast.error("Failed to fetch records.");
        } finally {
            setIsRefreshing(false);
        }
    };
    useEffect(() => {
        fetchRecords();
    }, []);

    // --- Fuse setup ---
    const fuse = useMemo(
        () =>
            new Fuse(records, {
                keys: ["domain", "company_name"],
                threshold: 0.1,
                ignoreLocation: true,
                includeScore: true,
            }),
        [records]
    );

    // --- Dropdown options ---
    const countryOptions = useMemo(() => {
        const uniq = Array.from(new Set(records.map((r) => r.country).filter(Boolean)));
        return uniq.map((c) => ({ value: c, label: c }));
    }, [records]);

    const categoryOptions = useMemo(() => {
        const uniq = Array.from(new Set(records.map((r) => r.business_category).filter(Boolean)));
        return uniq.map((c) => ({ value: c, label: c }));
    }, [records]);

    // --- Apply filters, search, sort ---
    useEffect(() => {
        let temp = searchTerm.trim() ? fuse.search(searchTerm).map((res) => res.item) : [...records];

        temp = applyFilter(temp, "country", countryFilter);
        temp = applyFilter(temp, "business_category", categoryFilter);
        if (selectedCRM.value !== "*") {
            temp = temp.filter((r) => (selectedCRM.value === "hasOwner" ? !!r.dynamics_owner : !r.dynamics_owner));
        }

        // Date-range filter (inclusive)
        const startB = startDate ? dayStartLocal(startDate) : null;
        const endB = endDate ? dayEndLocal(endDate) : null;
        if (startB || endB) {
            temp = temp.filter((r) => {
                const rd = parseRecordDate(r[dateField]);
                if (!rd) return false;
                if (startB && rd < startB) return false;
                if (endB && rd > endB) return false;
                return true;
            });
        }

        if (sortField) {
            temp.sort((a, b) => {
                const aRaw = a[sortField];
                const bRaw = b[sortField];

                let diff;
                if (sortField === "created_at" || sortField === "expanded_at") {
                    diff = new Date(aRaw) - new Date(bRaw);
                } else if (sortField === "relevance") {
                    diff = (aRaw ?? 0) - (bRaw ?? 0);
                } else {
                    diff = (aRaw ?? "")
                        .toString()
                        .localeCompare((bRaw ?? "").toString(), undefined, { sensitivity: "base" });
                }
                return sortOrder === "asc" ? diff : -diff;
            });
        }

        setFilteredRecords(temp);
    }, [
        records,
        searchTerm,
        countryFilter,
        categoryFilter,
        selectedCRM,
        fuse,
        sortField,
        sortOrder,
        startDate,
        endDate,
        dateField,
    ]);

    useEffect(() => {
        setCurrentPage(0);
    }, [searchTerm, countryFilter, categoryFilter, sortField, sortOrder, startDate, endDate, dateField]);

    // --- Pagination ---
    const pageCount = Math.ceil(filteredRecords.length / recordsPerPage);
    const displayedRecords = filteredRecords.slice(currentPage * recordsPerPage, (currentPage + 1) * recordsPerPage);

    // --- Column Sort handler (3-state) ---
    const handleSort = (field) => {
        if (sortField !== field) {
            setSortField(field);
            setSortOrder("asc");
        } else {
            if (sortOrder === "asc") setSortOrder("desc");
            else if (sortOrder === "desc") setSortField(null); // cancel sort
        }
    };

    // --- CRUD operations ---
    const handleDeleteRecord = async () => {
        if (!recordToDelete) return;
        try {
            await axiosInstanceExternal.delete(`/record/records/${recordToDelete.record_id}`);
            toast.success("Record deleted");
            setIsDeleteModalOpen(false);
            fetchRecords();
        } catch (err) {
            console.error(err);
            toast.error("Delete failed.");
        }
    };

    const handleCreateRecord = async () => {
        try {
            await axiosInstanceExternal.post("/record/records", newRecord);
            toast.success("Record created");
            setIsCreateModalOpen(false);
            fetchRecords();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.error || "");
        }
    };

    const handleUpdateRecord = async () => {
        try {
            await axiosInstanceExternal.put(`/record/records/${recordToUpdate.record_id}`, recordToUpdate);
            toast.success("Record updated");
            setIsUpdateModalOpen(false);
            fetchRecords();
        } catch (err) {
            console.error(err);
            toast.error("Update failed.");
        }
    };

    const handleInputChange = (e, updateMode = false) => {
        const { name, value } = e.target;
        if (updateMode) setRecordToUpdate((prev) => ({ ...prev, [name]: value }));
        else setNewRecord((prev) => ({ ...prev, [name]: value }));
    };

    const handleCreateLeads = async () => {
        if (!leadCreationEnabled) {
            toast.info("Lead creation is currently disabled.");
            return;
        }

        const recordIds = filteredRecords.map((r) => r.record_id);
        if (!recordIds.length) {
            toast.info("No records selected");
            return;
        }

        setLeadButtonDisabled(true);
        try {
            await axiosInstanceExternal.post("/record/create_leads_async", { record_ids: recordIds });
            toast.success("Lead creation started in background.");
        } catch (err) {
            const msg = err.response?.data?.error || err.message || "Unknown error";
            toast.error(`Failed to create leads: ${msg}`);
        } finally {
            setLeadButtonDisabled(false);
        }
    };

    // format date
    const formatDate = (date) => {
        if (!date) return "";
        const localDate = new Date(date.replace(/ GMT$/, ""));
        const day = String(localDate.getDate()).padStart(2, "0");
        const month = localDate.toLocaleString("en-AU", { month: "short" });
        const year = localDate.getFullYear();
        const hours = String(localDate.getHours()).padStart(2, "0");
        const minutes = String(localDate.getMinutes()).padStart(2, "0");
        return `${day} ${month}, ${year} ${hours}:${minutes}`;
    };

    const parseRecordDate = (d) => {
        if (!d) return null;
        const clean = d.replace(/ GMT$/, "");
        const dt = new Date(clean);
        return Number.isNaN(dt.getTime()) ? null : dt;
    };

    const dayStartLocal = (yyyyMmDd) => {
        const [y, m, d] = yyyyMmDd.split("-").map(Number);
        return new Date(y, m - 1, d, 0, 0, 0, 0);
    };

    const dayEndLocal = (yyyyMmDd) => {
        const [y, m, d] = yyyyMmDd.split("-").map(Number);
        return new Date(y, m - 1, d, 23, 59, 59, 999);
    };

    const handleEmailData = async () => {
        const formattedRecords = filteredRecords.map((rec) => ({
            ...rec,
            created_at: formatDate(rec.created_at),
        }));
        const emailData = { email, records: formattedRecords };
        setEmailButtonDisabled(true);
        try {
            const response = await axiosInstanceExternal.post("/record/send_email", emailData);
            if (response.status === 200) {
                toast.success(response.data.message || "Email sent successfully");
            } else {
                toast.error(response.data.message || "Failed to send email");
            }
        } catch (err) {
            toast.error("Failed to send email" + err.response?.data?.message);
        } finally {
            setEmailButtonDisabled(false);
        }
    };

    const handleRedo = async () => {
        const domainIds = filteredRecords.map((rec) => rec.record_id);
        if (domainIds.length === 0) {
            toast.error("No records to redo");
            return;
        }

        setExpandButtonDisabled(true);
        try {
            const response = await axiosInstanceExternal.post("/record/forceEnhanceExpand", { domain_ids: domainIds });
            if (response.status === 202) {
                toast.success(response.data.message || "Domains added to redo successfully");
            } else {
                toast.error(response.data.message || "Failed to add records to redo");
            }
        } catch (err) {
            toast.error("Failed to add records to redo" + err.response?.data?.message);
        } finally {
            setExpandButtonDisabled(false);
        }
    };

    const handleSingleRedo = async (recordId) => {
        setExpandButtonDisabled(true);
        try {
            const resp = await axiosInstanceExternal.post("/record/forceEnhanceExpand", { domain_ids: [recordId] });
            if (resp.status === 202) {
                toast.success(resp.data.message || "Domain added to redo");
            } else {
                toast.error(resp.data.message || "Failed to add to redo");
            }
        } catch (err) {
            toast.error("Failed to add to redo" + (err.response?.data?.message ? `: ${err.response.data.message}` : ""));
        } finally {
            setExpandButtonDisabled(false);
        }
    };

    const handleCreateSingleLead = async (recordId) => {
        if (!leadCreationEnabled) {
            toast.info("Lead creation is currently disabled.");
            return;
        }

        setLeadButtonDisabled(true);
        try {
            const { data } = await axiosInstanceExternal.post("/record/create_leads", { record_ids: [recordId] });
            const { created = [], skipped = [], errors = [] } = data;
            const parts = [];
            if (created.length) parts.push(`${created.length} lead${created.length > 1 ? "s" : ""} created`);
            if (skipped.length) parts.push(`${skipped.length} skipped`);
            if (errors.length) parts.push(`${errors.length} error${errors.length > 1 ? "s" : ""}`);
            toast.success(parts.join(", ") || "Done");
        } catch (err) {
            const msg = err.response?.data?.error || err.message || "Unknown error";
            toast.error(`Failed to create lead: ${msg}`);
        } finally {
            setLeadButtonDisabled(false);
        }
    };

    const FilterPopover = ({ btnText, filter, setFilter, options }) => {
        const [open, setOpen] = useState(false);
        const [draftOp, setDraftOp] = useState(filter.op);
        const [draftVals, setDraftVals] = useState(filter.values || []);
        const ref = useRef(null);

        useEffect(() => {
            if (open) {
                setDraftOp(filter.op);
                setDraftVals(filter.values || []);
            }
        }, [open, filter.op, filter.values]);

        useEffect(() => {
            const onDoc = (e) => {
                if (open && ref.current && !ref.current.contains(e.target)) setOpen(false);
            };
            document.addEventListener("mousedown", onDoc);
            return () => document.removeEventListener("mousedown", onDoc);
        }, [open]);

        const disabled = draftOp === "has" || draftOp === "not_has";

        return (
            <div className="relative">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Filter</label>
                <button
                    type="button"
                    className="h-11 inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 hover:bg-slate-50 hover:border-slate-300 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60"
                    onClick={() => setOpen((o) => !o)}
                >
                    Filter: {btnText}
                    <span className="ml-2 text-slate-400" aria-hidden="true">
                        ▾
                    </span>
                </button>

                {open && (
                    <div
                        ref={ref}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="absolute right-0 mt-2 w-80 rounded-2xl bg-white border border-slate-200 shadow-xl p-4 z-20"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div className="font-extrabold text-slate-900">Filter by {btnText}</div>
                            <button
                                className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                                onClick={() => setOpen(false)}
                                type="button"
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="space-y-3">
                            <Select
                                value={OP_OPTIONS.find((o) => o.value === draftOp)}
                                onChange={(op) => setDraftOp(op.value)}
                                options={OP_OPTIONS}
                                classNamePrefix="react-select"
                                placeholder="Equals"
                                styles={{
                                    control: (p) => ({
                                        ...p,
                                        minHeight: 44,
                                        height: 44,
                                        borderColor: "#e2e8f0",
                                        boxShadow: "none",
                                        borderRadius: 12,
                                        "&:hover": { borderColor: "#cbd5e1" },
                                    }),
                                    option: (p, s) => ({
                                        ...p,
                                        backgroundColor: s.isSelected ? "#0f172a" : s.isFocused ? "#f1f5f9" : undefined,
                                        color: s.isSelected ? "white" : "#0f172a",
                                    }),
                                    menu: (p) => ({ ...p, borderRadius: 12, overflow: "hidden" }),
                                }}
                            />
                            <Select
                                isMulti
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                isDisabled={disabled}
                                value={draftVals}
                                onChange={(vals) => setDraftVals(vals || [])}
                                options={options}
                                classNamePrefix="react-select"
                                placeholder="Select…"
                                isClearable={false}
                                styles={{
                                    control: (p) => ({
                                        ...p,
                                        minHeight: 44,
                                        borderColor: "#e2e8f0",
                                        boxShadow: "none",
                                        borderRadius: 12,
                                        "&:hover": { borderColor: "#cbd5e1" },
                                        opacity: disabled ? 0.65 : 1,
                                    }),
                                    option: (p, s) => ({
                                        ...p,
                                        backgroundColor: s.isSelected ? "#0f172a" : s.isFocused ? "#f1f5f9" : undefined,
                                        color: s.isSelected ? "white" : "#0f172a",
                                    }),
                                    menu: (p) => ({ ...p, borderRadius: 12, overflow: "hidden" }),
                                }}
                            />
                        </div>

                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                className="px-3 py-2 text-sm font-semibold rounded-xl bg-slate-100 text-slate-800 hover:bg-slate-200 transition focus:outline-none focus:ring-2 focus:ring-slate-300"
                                onClick={() => setDraftVals([])}
                                type="button"
                            >
                                Clear
                            </button>
                            <button
                                className="px-4 py-2 text-sm font-semibold rounded-xl bg-slate-900 text-white hover:bg-slate-800 transition focus:outline-none focus:ring-2 focus:ring-slate-400/60"
                                onClick={() => {
                                    setFilter({ op: draftOp, values: draftVals });
                                    setOpen(false);
                                }}
                                type="button"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const DateRangePopover = ({
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        dateField,
        setDateField,
        dateFieldChosen,
        setDateFieldChosen,
    }) => {
        const [open, setOpen] = useState(false);
        const [draftStart, setDraftStart] = useState(startDate || "");
        const [draftEnd, setDraftEnd] = useState(endDate || "");
        const [draftField, setDraftField] = useState(dateField || "created_at");
        const popoverRef = useRef(null);

        useEffect(() => {
            if (open) {
                setDraftStart(startDate || "");
                setDraftEnd(endDate || "");
                setDraftField(dateField || "created_at");
            }
        }, [open, startDate, endDate, dateField]);

        useEffect(() => {
            if (!open) return;
            const onDocMouseDown = (e) => {
                const insidePopover = popoverRef.current?.contains(e.target);
                const insideSelectPortal = e.target.closest(".drp__menu") || e.target.closest(".drp__menu-portal");
                if (!insidePopover && !insideSelectPortal) setOpen(false);
            };
            document.addEventListener("mousedown", onDocMouseDown);
            return () => document.removeEventListener("mousedown", onDocMouseDown);
        }, [open]);

        const fmt = (d) => {
            if (!d) return "";
            const [y, m, dd] = d.split("-").map(Number);
            return new Date(y, m - 1, dd).toLocaleDateString("en-AU", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });
        };

        const OPTIONS = [
            { value: "created_at", label: "Created" },
            { value: "expanded_at", label: "Expanded" },
        ];
        const fieldLabel =
            OPTIONS.find((o) => o.value === (dateFieldChosen ? dateField : draftField))?.label || "Date";
        const prefix = dateFieldChosen ? `Filter by ${fieldLabel}` : "Filter by Date";
        const suffix =
            startDate || endDate ? `: ${startDate ? fmt(startDate) : "—"}–${endDate ? fmt(endDate) : "—"}` : "";
        const buttonLabel = `${prefix}${suffix}`;

        return (
            <div className="relative">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Date range</label>
                <button
                    type="button"
                    className="h-11 inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 hover:bg-slate-50 hover:border-slate-300 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60"
                    onClick={() => setOpen(true)}
                >
                    {buttonLabel}
                    <span className="ml-2 text-slate-400" aria-hidden="true">
                        ▾
                    </span>
                </button>

                {open && (
                    <div
                        ref={popoverRef}
                        className="absolute right-0 z-[9999] mt-2 w-80 rounded-2xl border border-slate-200 bg-white p-4 shadow-xl"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div className="font-extrabold text-slate-900">Date range</div>
                            <button
                                className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                                onClick={() => setOpen(false)}
                                type="button"
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="space-y-3" onMouseDown={(e) => e.stopPropagation()}>
                            <Select
                                value={OPTIONS.find((o) => o.value === draftField)}
                                onChange={(opt) => setDraftField(opt.value)}
                                options={OPTIONS}
                                classNamePrefix="drp"
                                placeholder="Choose field"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                closeMenuOnSelect={true}
                                styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 10000 }),
                                    control: (p) => ({
                                        ...p,
                                        minHeight: 44,
                                        height: 44,
                                        borderColor: "#e2e8f0",
                                        boxShadow: "none",
                                        borderRadius: 12,
                                        "&:hover": { borderColor: "#cbd5e1" },
                                    }),
                                    option: (p, s) => ({
                                        ...p,
                                        backgroundColor: s.isSelected ? "#0f172a" : s.isFocused ? "#f1f5f9" : undefined,
                                        color: s.isSelected ? "white" : "#0f172a",
                                    }),
                                    menu: (p) => ({ ...p, borderRadius: 12, overflow: "hidden" }),
                                }}
                            />
                            <input
                                type="date"
                                value={draftStart}
                                onChange={(e) => setDraftStart(e.target.value)}
                                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-slate-300 focus:ring-2 focus:ring-slate-400/40"
                            />
                            <input
                                type="date"
                                value={draftEnd}
                                onChange={(e) => setDraftEnd(e.target.value)}
                                className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-slate-300 focus:ring-2 focus:ring-slate-400/40"
                            />
                        </div>

                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                type="button"
                                className="px-3 py-2 text-sm font-semibold rounded-xl bg-slate-100 text-slate-800 hover:bg-slate-200 transition focus:outline-none focus:ring-2 focus:ring-slate-300"
                                onClick={() => {
                                    setDraftStart("");
                                    setDraftEnd("");
                                    setDraftField(dateField || "created_at");
                                }}
                            >
                                Clear
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2 text-sm font-semibold rounded-xl bg-slate-900 text-white hover:bg-slate-800 transition focus:outline-none focus:ring-2 focus:ring-slate-400/60"
                                onClick={() => {
                                    setDateField(draftField);
                                    setDateFieldChosen(true);
                                    setStartDate(draftStart);
                                    setEndDate(draftEnd);
                                    setOpen(false);
                                }}
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // --- Shared react-select style (44px controls) ---
    const selectStyles44 = {
        control: (p) => ({
            ...p,
            minHeight: 44,
            height: 44,
            borderColor: "#e2e8f0",
            boxShadow: "none",
            borderRadius: 12,
            "&:hover": { borderColor: "#cbd5e1" },
        }),
        valueContainer: (p) => ({ ...p, height: 44 }),
        input: (p) => ({ ...p, margin: 0, padding: 0 }),
        option: (p, s) => ({
            ...p,
            backgroundColor: s.isSelected ? "#0f172a" : s.isFocused ? "#f1f5f9" : undefined,
            color: s.isSelected ? "white" : "#0f172a",
        }),
        menu: (p) => ({ ...p, borderRadius: 12, overflow: "hidden" }),
        menuPortal: (base) => ({ ...base, zIndex: 10000 }),
    };

    // --- Render ---
    return (
        <div className="min-h-screen bg-gray-100 px-3 sm:px-4 md:px-6 py-6">
            <div className="w-full max-w-full mx-auto">
                <div className="flex flex-col gap-5 sm:gap-6">
                    {/* Header */}
                    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="p-4 sm:p-6">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <h1 className="text-lg sm:text-xl font-extrabold text-slate-900 tracking-tight">
                                        Domain Management
                                    </h1>
                                    <p className="mt-1 text-sm text-slate-600">
                                        Manage domains, apply filters, and take actions without leaving the table.
                                    </p>
                                </div>

                                <div className="inline-flex items-center gap-2">
                                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700">
                                        {hasFilters
                                            ? `Filtered: ${filteredRecords.length} / ${records.length}`
                                            : `Total: ${records.length}`}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Actions */}
                    {/* <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="p-4 sm:p-6">
                            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 sm:gap-3">
                                <button
                                    onClick={() => setIsCreateModalOpen(true)}
                                    className="w-full inline-flex items-center justify-center px-4 py-2 rounded-xl bg-slate-900 text-white font-semibold shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 transition text-sm sm:text-base"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5 mr-2 shrink-0"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    Create
                                </button>

                                <button
                                    onClick={handleEmailData}
                                    className="w-full inline-flex items-center justify-center px-4 py-2 rounded-xl bg-slate-900 text-white font-semibold shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                                    disabled={emailButtonDisabled}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5 mr-2 shrink-0"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                    </svg>
                                    Email me data
                                </button>

                                <button
                                    onClick={handleRedo}
                                    className="w-full inline-flex items-center justify-center px-4 py-2 rounded-xl bg-slate-900 text-white font-semibold shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                                    disabled={expandButtonDisabled}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5 mr-2 shrink-0"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    Enhance &amp; expand
                                </button>

                                <button
                                    onClick={handleCreateLeads}
                                    className="w-full inline-flex items-center justify-center px-4 py-2 rounded-xl bg-slate-900 text-white font-semibold shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                                    disabled={leadButtonDisabled || !leadCreationEnabled}
                                    type="button"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5 mr-2 shrink-0"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    Create leads
                                </button>

                                <button
                                    onClick={fetchRecords}
                                    disabled={isRefreshing}
                                    className="w-full inline-flex items-center justify-center px-4 py-2 rounded-xl bg-slate-900 text-white font-semibold shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5 mr-2 shrink-0"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <polyline points="23 4 23 10 17 10" />
                                        <polyline points="1 20 1 14 7 14" />
                                        <path d="M3.51 9a9 9 0 0 1 14.13-3.36" />
                                        <path d="M20.49 15a9 9 0 0 1-14.13 3.36" />
                                    </svg>
                                    {isRefreshing ? "Refreshing…" : "Refresh"}
                                </button>

                                <button
                                    onClick={() => (window.location.href = "/seed-insight")}
                                    className="w-full inline-flex items-center justify-center px-4 py-2 rounded-xl bg-slate-900 text-white font-semibold shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                                >
                                    Seed insight
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5 ml-2 shrink-0"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </section> */}

                    {/* Filters */}
                    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="p-4 sm:p-6">
                            <div className="flex flex-wrap items-end gap-2 sm:gap-3 lg:gap-4">
                                <div className="flex-[2] min-w-[260px] w-full">
                                    <label className="block text-xs font-semibold text-slate-600 mb-1">Search</label>
                                    <input
                                        type="text"
                                        placeholder="Search domain or company…"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm sm:text-base text-slate-900 placeholder:text-slate-400
                      focus:ring-2 focus:ring-slate-400/40 focus:border-slate-300 transition"
                                    />
                                </div>

                                <FilterPopover
                                    btnText="Category"
                                    filter={categoryFilter}
                                    setFilter={setCategoryFilter}
                                    options={categoryOptions}
                                />

                                <FilterPopover
                                    btnText="Country"
                                    filter={countryFilter}
                                    setFilter={setCountryFilter}
                                    options={countryOptions}
                                />

                                <DateRangePopover
                                    startDate={startDate}
                                    endDate={endDate}
                                    setStartDate={setStartDate}
                                    setEndDate={setEndDate}
                                    dateField={dateField}
                                    setDateField={setDateField}
                                    dateFieldChosen={dateFieldChosen}
                                    setDateFieldChosen={setDateFieldChosen}
                                />

                                <div className="w-56 sm:w-64">
                                    <label className="block text-xs font-semibold text-slate-600 mb-1">CRM Status</label>
                                    <Select
                                        value={selectedCRM}
                                        onChange={setSelectedCRM}
                                        options={crmOptions}
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        placeholder="Filter by CRM…"
                                        styles={selectStyles44}
                                    />
                                </div>
                            </div>

                            <div className="mt-4 text-sm text-slate-600">
                                {hasFilters
                                    ? `Showing ${filteredRecords.length} of ${records.length} record${records.length === 1 ? "" : "s"
                                    }.`
                                    : `Total records: ${records.length}.`}
                            </div>
                        </div>
                    </section>

                    {/* Table */}
                    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="p-4 sm:p-6">
                            {/* Desktop */}
                            <div className="hidden md:block overflow-x-auto rounded-xl border border-slate-200">
                                <table className="min-w-full table-fixed divide-y divide-slate-200">
                                    <thead className="bg-slate-50">
                                        <tr>
                                            {[
                                                "domain",
                                                "company_name",
                                                "business_category",
                                                "country",
                                                "relevance",
                                                "authority",
                                                "dynamics_crm",
                                                "expanded_at",
                                                "created_at",
                                            ].map((col) => {
                                                const isSortable = col !== "dynamics_crm";
                                                const active = sortField === col;
                                                return (
                                                    <th
                                                        key={col}
                                                        scope="col"
                                                        {...(isSortable && { onClick: () => handleSort(col) })}
                                                        className={[
                                                            "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700",
                                                            isSortable ? "cursor-pointer select-none hover:bg-slate-100" : "cursor-default",
                                                            active ? "bg-slate-100" : "",
                                                        ].join(" ")}
                                                    >
                                                        <span className="inline-flex items-center gap-1">
                                                            {col.replace(/_/g, " ")}
                                                            {isSortable && active && (
                                                                <span className="text-slate-900">
                                                                    {sortOrder === "asc" ? "↑" : "↓"}
                                                                </span>
                                                            )}
                                                        </span>
                                                    </th>
                                                );
                                            })}
                                            {/* <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700">
                                                Actions
                                            </th> */}
                                        </tr>
                                    </thead>

                                    <tbody className="divide-y divide-slate-200 bg-white text-sm text-slate-900">
                                        {displayedRecords.map((rec) => (
                                            <tr
                                                key={rec.record_id}
                                                className="group cursor-pointer hover:bg-slate-50"
                                                onClick={() => {
                                                    setRecordDetails(rec);
                                                    setIsDetailsModalOpen(true);
                                                }}
                                            >
                                                <td className="px-4 py-4 font-medium max-w-[240px] truncate">
                                                    {rec.domain && rec.domain !== "N/A" ? (
                                                        <a
                                                            href={rec.domain.startsWith("http") ? rec.domain : `https://${rec.domain}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-900"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            {rec.domain}
                                                        </a>
                                                    ) : (
                                                        "N/A"
                                                    )}
                                                </td>
                                                <td className="px-4 py-4 max-w-[240px] truncate">{rec.company_name}</td>
                                                <td className="px-4 py-4 whitespace-nowrap text-slate-700">{rec.business_category}</td>
                                                <td className="px-4 py-4 whitespace-nowrap text-slate-700">{rec.country}</td>
                                                <td className="px-4 py-4 whitespace-nowrap text-slate-700">{rec.relevance}</td>
                                                <td className="px-4 py-4 whitespace-nowrap text-slate-700">{rec.authority}</td>
                                                <td className="px-4 py-4">
                                                    <span
                                                        className={[
                                                            "inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-semibold",
                                                            rec.dynamics_owner
                                                                ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                                                                : "bg-rose-50 text-rose-700 ring-1 ring-rose-200",
                                                        ].join(" ")}
                                                    >
                                                        {rec.dynamics_owner ? "✔️" : "❌"}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-4 whitespace-nowrap text-slate-700">{formatDate(rec.expanded_at)}</td>
                                                <td className="px-4 py-4 whitespace-nowrap text-slate-700">{formatDate(rec.created_at)}</td>

                                                {/* <td className="px-4 py-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                                                    <div className="flex justify-end gap-2">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setRecordToUpdate(rec);
                                                                setIsUpdateModalOpen(true);
                                                            }}
                                                            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition"
                                                            title="Update record"
                                                            type="button"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-7.293 7.293A1 1 0 018.001 14H5a1 1 0 01-1-1v-3a1 1 0 01.293-.707l7.293-7.293zM14 6L11 9l-2-2 3-3 2 2z" />
                                                                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                                            </svg>
                                                        </button>

                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setRecordToDelete(rec);
                                                                setIsDeleteModalOpen(true);
                                                            }}
                                                            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-400/60 transition"
                                                            title="Delete record"
                                                            type="button"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M9 2a1 1 0 00-1 1v1H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-4V3a1 1 0 00-1-1H9zm1 2H9v1h2V4zm0 2H9v6h2V6z"
                                                                    clipRule="evenodd"
                                                                />
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M10 2a1 1 0 011 1v1h4a1 1 0 010 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V6a1 1 0 010-2h4V3a1 1 0 011-1z"
                                                                    clipRule="evenodd"
                                                                />
                                                            </svg>
                                                        </button>

                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleSingleRedo(rec.record_id);
                                                            }}
                                                            disabled={expandButtonDisabled}
                                                            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                                            title="Force enhance & expand"
                                                            type="button"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                                                                    clipRule="evenodd"
                                                                />
                                                            </svg>
                                                        </button>

                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleCreateSingleLead(rec.record_id);
                                                            }}
                                                            disabled={leadButtonDisabled || !leadCreationEnabled}
                                                            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                                            title="Create CRM lead"
                                                            type="button"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td> */}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile cards */}
                            <div className="md:hidden space-y-3">
                                {displayedRecords.map((rec) => (
                                    <div
                                        key={rec.record_id}
                                        className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:bg-slate-50 cursor-pointer"
                                        onClick={() => {
                                            setRecordDetails(rec);
                                            setIsDetailsModalOpen(true);
                                        }}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <p className="text-sm text-slate-700">
                                                    <span className="font-semibold text-slate-900">Domain:</span>{" "}
                                                    {rec.domain && rec.domain !== "N/A" ? (
                                                        <a
                                                            href={rec.domain.startsWith("http") ? rec.domain : `https://${rec.domain}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="break-all text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-900"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            {rec.domain}
                                                        </a>
                                                    ) : (
                                                        "N/A"
                                                    )}
                                                </p>
                                                <p className="mt-1 text-sm text-slate-700">
                                                    <span className="font-semibold text-slate-900">Company:</span> {rec.company_name}
                                                </p>
                                            </div>

                                            <span
                                                className={[
                                                    "shrink-0 inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1",
                                                    rec.dynamics_owner
                                                        ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                                                        : "bg-rose-50 text-rose-700 ring-rose-200",
                                                ].join(" ")}
                                            >
                                                {rec.dynamics_owner ? "Recorded" : "Not recorded"}
                                            </span>
                                        </div>

                                        <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-slate-700">
                                            <div><span className="font-semibold text-slate-900">Category:</span> {rec.business_category}</div>
                                            <div><span className="font-semibold text-slate-900">Country:</span> {rec.country}</div>
                                            <div><span className="font-semibold text-slate-900">Relevance:</span> {rec.relevance}%</div>
                                            <div><span className="font-semibold text-slate-900">Authority:</span> {rec.authority}</div>
                                            <div className="col-span-2"><span className="font-semibold text-slate-900">Expanded:</span> {formatDate(rec.expanded_at)}</div>
                                            <div className="col-span-2"><span className="font-semibold text-slate-900">Created:</span> {formatDate(rec.created_at)}</div>
                                        </div>

                                        {/* <div className="mt-4 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                                            <button
                                                onClick={() => {
                                                    setRecordToUpdate(rec);
                                                    setIsUpdateModalOpen(true);
                                                }}
                                                className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition"
                                                title="Update record"
                                                type="button"
                                            >
                                                Update
                                            </button>

                                            <button
                                                onClick={() => {
                                                    setRecordToDelete(rec);
                                                    setIsDeleteModalOpen(true);
                                                }}
                                                className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-rose-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-rose-400/60 transition"
                                                title="Delete record"
                                                type="button"
                                            >
                                                Delete
                                            </button>

                                            <button
                                                onClick={() => handleSingleRedo(rec.record_id)}
                                                disabled={expandButtonDisabled}
                                                className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                                title="Force enhance & expand"
                                                type="button"
                                            >
                                                Enhance &amp; expand
                                            </button>

                                            <button
                                                onClick={() => handleCreateSingleLead(rec.record_id)}
                                                disabled={leadButtonDisabled || !leadCreationEnabled}
                                                className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                                title="Create CRM lead"
                                                type="button"
                                            >
                                                Create lead
                                            </button>
                                        </div> */}
                                    </div>
                                ))}
                            </div>

                            {/* Pagination */}
                            <div className="mt-6">
                                <ReactPaginate
                                    previousLabel={
                                        <span className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition">
                                            ← Previous
                                        </span>
                                    }
                                    nextLabel={
                                        <span className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition">
                                            Next →
                                        </span>
                                    }
                                    pageCount={pageCount}
                                    onPageChange={({ selected }) => setCurrentPage(selected)}
                                    containerClassName="flex flex-wrap items-center justify-center gap-2"
                                    pageClassName="block"
                                    pageLinkClassName="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition"
                                    activeLinkClassName="!bg-slate-900 !text-white !border-slate-900"
                                    disabledClassName="opacity-50 cursor-not-allowed"
                                    breakClassName="px-2 py-2 text-slate-400"
                                    renderOnZeroPageCount={null}
                                />
                            </div>
                        </div>
                    </section>

                    {/* Create Modal */}
                    <ReactModal
                        isOpen={isCreateModalOpen}
                        onRequestClose={() => setIsCreateModalOpen(false)}
                        className="bg-white p-4 sm:p-6 rounded-2xl shadow-xl w-[min(40rem,calc(100vw-2rem))] max-h-[80vh] overflow-y-auto outline-none border border-slate-200"
                        overlayClassName="fixed inset-0 bg-black/40 flex justify-center items-center px-3"
                    >
                        <div className="flex items-start justify-between gap-3 mb-4">
                            <div>
                                <h2 className="text-lg sm:text-xl font-extrabold text-slate-900">Create New Record</h2>
                                <p className="text-sm text-slate-600 mt-1">Enter fields and create a new record.</p>
                            </div>
                            <button
                                onClick={() => setIsCreateModalOpen(false)}
                                className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                                aria-label="Close"
                                type="button"
                            >
                                ✕
                            </button>
                        </div>

                        {renderFields(newRecord, false)}

                        <div className="mt-4 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                            <button
                                onClick={() => setIsCreateModalOpen(false)}
                                className="py-2 px-4 rounded-xl bg-slate-100 font-semibold text-slate-800 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 transition"
                                type="button"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateRecord}
                                className="py-2 px-4 rounded-xl bg-slate-900 font-semibold text-white shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400/60 transition"
                                type="button"
                            >
                                Create
                            </button>
                        </div>
                    </ReactModal>

                    {/* Update Modal */}
                    <ReactModal
                        isOpen={isUpdateModalOpen}
                        onRequestClose={() => setIsUpdateModalOpen(false)}
                        className="bg-white p-4 sm:p-6 rounded-2xl shadow-xl w-[min(40rem,calc(100vw-2rem))] max-h-[80vh] overflow-y-auto outline-none border border-slate-200"
                        overlayClassName="fixed inset-0 bg-black/40 flex justify-center items-center px-3"
                    >
                        <div className="flex items-start justify-between gap-3 mb-4">
                            <div>
                                <h2 className="text-lg sm:text-xl font-extrabold text-slate-900">Update Record</h2>
                                <p className="text-sm text-slate-600 mt-1">Edit fields and save changes.</p>
                            </div>
                            <button
                                onClick={() => setIsUpdateModalOpen(false)}
                                className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                                aria-label="Close"
                                type="button"
                            >
                                ✕
                            </button>
                        </div>

                        {renderFields(recordToUpdate, true)}

                        <div className="mt-4 flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                            <button
                                onClick={() => setIsUpdateModalOpen(false)}
                                className="py-2 px-4 rounded-xl bg-slate-100 font-semibold text-slate-800 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 transition"
                                type="button"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateRecord}
                                className="py-2 px-4 rounded-xl bg-amber-600 font-semibold text-white shadow-sm hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-300 transition"
                                type="button"
                            >
                                Update
                            </button>
                        </div>
                    </ReactModal>

                    {/* Details Modal */}
                    <ReactModal
                        isOpen={isDetailsModalOpen}
                        onRequestClose={() => setIsDetailsModalOpen(false)}
                        className="bg-white p-4 sm:p-6 rounded-2xl shadow-xl w-[min(44rem,calc(100vw-2rem))] max-h-[80vh] overflow-y-auto outline-none border border-slate-200"
                        overlayClassName="fixed inset-0 bg-black/40 flex justify-center items-center px-3"
                    >
                        <div className="flex items-start justify-between gap-3 mb-4">
                            <div>
                                <h2 className="text-lg sm:text-xl font-extrabold text-slate-900">Record Details</h2>
                                <p className="text-sm text-slate-600 mt-1">Read-only view of the record.</p>
                            </div>
                            <button
                                onClick={() => setIsDetailsModalOpen(false)}
                                className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                                aria-label="Close"
                                type="button"
                            >
                                ✕
                            </button>
                        </div>

                        {renderFields(recordDetails, false, true)}

                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={() => setIsDetailsModalOpen(false)}
                                className="py-2 px-4 rounded-xl bg-slate-100 font-semibold text-slate-800 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 transition"
                                type="button"
                            >
                                Close
                            </button>
                        </div>
                    </ReactModal>

                    {/* Delete Modal */}
                    <ReactModal
                        isOpen={isDeleteModalOpen}
                        onRequestClose={() => setIsDeleteModalOpen(false)}
                        className="bg-white p-4 sm:p-6 rounded-2xl shadow-xl w-[min(28rem,calc(100vw-2rem))] outline-none border border-slate-200"
                        overlayClassName="fixed inset-0 bg-black/40 flex justify-center items-center px-3"
                    >
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <h2 className="text-lg sm:text-xl font-extrabold text-slate-900">Confirm Deletion</h2>
                            <button
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="rounded-xl px-3 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
                                aria-label="Close"
                                type="button"
                            >
                                ✕
                            </button>
                        </div>

                        <p className="text-slate-700 mb-5 text-sm sm:text-base">
                            Are you sure you want to delete this record? This action cannot be undone.
                        </p>

                        <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                            <button
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="py-2 px-4 rounded-xl bg-slate-100 text-slate-800 font-semibold hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 transition"
                                type="button"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteRecord}
                                className="py-2 px-4 rounded-xl bg-red-600 text-white font-semibold shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300 transition"
                                type="button"
                            >
                                Delete
                            </button>
                        </div>
                    </ReactModal>
                </div>
            </div>
        </div>
    );

    // --- Field renderer helper ---
    function renderFields(record, updateMode, readOnly = false) {
        const fields = [
            { label: "Domain", name: "domain", isLink: true },
            { label: "Company Name", name: "company_name" },
            { label: "Summary", name: "summary" },
            { label: "Emails", name: "emails" },
            { label: "Phone", name: "phone" },
            { label: "LinkedIn", name: "linkedin", isLink: true },
            { label: "Street Address", name: "street_address" },
            { label: "City", name: "city" },
            { label: "State/Province", name: "state_province" },
            { label: "Zip/Postal Code", name: "zip_postal_code" },
            { label: "Country", name: "country" },
            { label: "Business Category", name: "business_category" },
            { label: "Keywords", name: "keywords" },
            { label: "Search Terms", name: "search_terms" },
            { label: "Relevance", name: "relevance" },
            { label: "Research Similar Flag", name: "research_similar_flag" },
        ];

        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {fields.map((field) => {
                    const value = record[field.name] ?? "";

                    if (readOnly && field.isLink && value) {
                        return (
                            <div key={field.name} className="sm:col-span-2">
                                <label className="block text-xs font-semibold text-slate-600 mb-1">{field.label}</label>
                                <a
                                    href={value.startsWith("http") ? value : `https://${value}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block w-full break-all rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-500"
                                >
                                    {value}
                                </a>
                            </div>
                        );
                    }

                    return (
                        <div key={field.name} className={field.name === "summary" ? "sm:col-span-2" : ""}>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">{field.label}</label>
                            <input
                                name={field.name}
                                type="text"
                                value={value}
                                onChange={(e) => !readOnly && handleInputChange(e, updateMode)}
                                readOnly={readOnly}
                                className={[
                                    "h-11 w-full rounded-xl border px-3 py-2 text-sm text-slate-900 transition",
                                    readOnly
                                        ? "border-slate-200 bg-slate-100 text-slate-500 cursor-not-allowed"
                                        : "border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/40 focus:border-slate-300",
                                ].join(" ")}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }
};

export default DomainManagement;
